import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UserRole } from "@/types";
import type { ConsoleAuthenticatedUser } from "@/types/api";
import { usePingStore } from "@/stores/ping-store";

type User = ConsoleAuthenticatedUser;

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshingPermissions: boolean;
  arePermissionsVerified: boolean;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setUserRole: (role: UserRole) => void;
  updateUserProfile: (updates: Partial<User>) => void;
  refreshCurrentUser: () => Promise<User | null>;
  clearAuth: () => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: readonly string[]) => boolean;
  // Role detection helpers
  getUserRole: () => UserRole | null;
  isPatient: () => boolean;
  isCareManager: () => boolean;
  isStaffAdmin: () => boolean;
  isNutritionist: () => boolean;
  isPhysiotherapist: () => boolean;
}

const normalizePermissions = (permissions: unknown): string[] => {
  if (!Array.isArray(permissions)) {
    return [];
  }

  return Array.from(
    new Set(
      permissions.filter(
        (permission): permission is string =>
          typeof permission === "string" && permission.trim().length > 0
      )
    )
  ).sort();
};

const normalizeUser = (user: Partial<User>): User => {
  const role = user.role || (user.user_type as UserRole | undefined);

  return {
    ...user,
    role,
    permissions: normalizePermissions(user.permissions),
  } as User;
};

const getRoleFromUser = (user: User | null): UserRole | null =>
  user?.role || (user?.user_type as UserRole) || null;

const syncPingRole = (user: User | null) => {
  const role = getRoleFromUser(user);
  usePingStore.getState().setCurrentUserRole(role);
};

let authSessionVersion = 0;

const resetPingState = () => {
  usePingStore.getState().resetPingState();
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isRefreshingPermissions: false,
      arePermissionsVerified: false,
      login: (user, accessToken, refreshToken) => {
        authSessionVersion += 1;
        const normalizedUser = normalizeUser(user);
        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("refresh_token", refreshToken);
        set({
          user: normalizedUser,
          isAuthenticated: true,
          isLoading: false,
          isRefreshingPermissions: false,
          arePermissionsVerified: true,
        });

        // Clear user-specific query cache on new login to prevent data leakage between users
        // We'll handle this in the component that uses the QueryClient
        window.dispatchEvent(new CustomEvent("auth:login"));

        // Initialize ping store with user role if available
        syncPingRole(normalizedUser);
      },
      logout: () => {
        authSessionVersion += 1;
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isRefreshingPermissions: false,
          arePermissionsVerified: false,
        });

        // Clear user-specific query cache on logout to prevent data persistence
        // We'll handle this in the component that uses the QueryClient
        window.dispatchEvent(new CustomEvent("auth:logout"));

        resetPingState();
      },
      setLoading: (loading) => set({ isLoading: loading }),
      setUserRole: (role) =>
        set((state) => ({
          user: state.user ? normalizeUser({ ...state.user, role }) : null,
        })),
      updateUserProfile: (updates) =>
        set((state) => ({
          user: state.user
            ? normalizeUser({ ...state.user, ...updates })
            : null,
        })),
      refreshCurrentUser: async () => {
        const accessToken = localStorage.getItem("access_token");
        const hasRefreshToken = Boolean(localStorage.getItem("refresh_token"));
        const sessionVersion = authSessionVersion;

        if (!accessToken || !hasRefreshToken) {
          return null;
        }

        set({ isRefreshingPermissions: true });
        try {
          const { api } = await import("@/lib/api");
          const response = await api.get<User>("/users/");

          if (
            sessionVersion !== authSessionVersion ||
            localStorage.getItem("access_token") === null
          ) {
            return null;
          }

          const normalizedUser = normalizeUser(response.data);

          set({
            user: normalizedUser,
            isAuthenticated: true,
            arePermissionsVerified: true,
          });
          syncPingRole(normalizedUser);

          return normalizedUser;
        } finally {
          if (sessionVersion === authSessionVersion) {
            set({ isRefreshingPermissions: false });
          }
        }
      },
      clearAuth: () => {
        authSessionVersion += 1;
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isRefreshingPermissions: false,
          arePermissionsVerified: false,
        });

        // Clear user-specific query cache when clearing auth
        window.dispatchEvent(new CustomEvent("auth:logout"));
        resetPingState();
      },
      hasPermission: (permission) => {
        const { arePermissionsVerified, user } = get();
        if (!arePermissionsVerified) {
          return false;
        }
        return Boolean(user?.permissions.includes(permission));
      },
      hasAnyPermission: (permissions) => {
        const { arePermissionsVerified, user } = get();
        if (!arePermissionsVerified || !user || permissions.length === 0) {
          return false;
        }

        return permissions.some((permission) =>
          user.permissions.includes(permission)
        );
      },

      // Role detection helpers
      getUserRole: () => {
        return getRoleFromUser(get().user);
      },
      isPatient: () => {
        return getRoleFromUser(get().user) === "patient";
      },
      isCareManager: () => {
        return getRoleFromUser(get().user) === "care_manager";
      },
      isStaffAdmin: () => {
        return getRoleFromUser(get().user) === "staff_admin";
      },
      isNutritionist: () => {
        return getRoleFromUser(get().user) === "nutritionist";
      },
      isPhysiotherapist: () => {
        return getRoleFromUser(get().user) === "physiotherapist";
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user ? normalizeUser(state.user) : null,
        isAuthenticated: state.isAuthenticated,
      }),
      version: 1,
      migrate: (persistedState: unknown) => {
        if (
          persistedState &&
          typeof persistedState === "object" &&
          "user" in persistedState
        ) {
          const state = persistedState as Partial<AuthState>;

          return {
            ...state,
            user: state.user ? normalizeUser(state.user) : null,
            arePermissionsVerified: false,
            isRefreshingPermissions: false,
          };
        }

        return persistedState;
      },
    }
  )
);

// Force logout function for API interceptor
export const forceLogoutAndRedirect = () => {
  const { clearAuth } = useAuthStore.getState();
  clearAuth();
  window.location.href = "/";
};
